import { FC, useState } from "react";
import { useFormik } from "formik";
import {
  Alert,
  Box,
  FormHelperText,
  Snackbar,
  SnackbarOrigin,
  TextField,
} from "@mui/material";
import * as Yup from "yup";
import classes from "./login.module.scss";
import { useAuthContext } from "../../context/AuthContext";
import LoadingButton from "@mui/lab/LoadingButton";

export interface Snack extends SnackbarOrigin {
  open: boolean;
}

const Login: FC = (props) => {
  const { login } = useAuthContext();
  const [snack, setSnack] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const openSnackbar = (message: string) => {
    setSnack(true);
    setError(message);
  };

  const closeSnackbar = () => {
    setSnack(false);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Email is required"),
      password: Yup.string().max(255).required("Password is required"),
    }),
    onSubmit: async (params): Promise<void> => {
      try {
        setLoading(true);
        await login(params.email, params.password);
      } catch (error: any) {
        if (error.response.status === 403) {
          openSnackbar("Password does not match");
        }
        if (error.response.status === 404) {
          openSnackbar("User does not exist");
        }
        setLoading(false);
      }
    },
  });

  return (
    <div>
      <h2 className={classes.loginHeader}>Sign in</h2>
      <form noValidate onSubmit={formik.handleSubmit} {...props}>
        <TextField
          autoFocus
          error={Boolean(formik.touched.email && formik.errors.email)}
          fullWidth
          helperText={formik.touched.email && formik.errors.email}
          label="Email Address"
          margin="normal"
          name="email"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          type="email"
          color="primary"
          value={formik.values.email}
        />
        <TextField
          error={Boolean(formik.touched.password && formik.errors.password)}
          fullWidth
          helperText={formik.touched.password && formik.errors.password}
          label="Password"
          margin="normal"
          name="password"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          type="password"
          value={formik.values.password}
        />
        {formik.errors.submit && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>{formik.errors.submit}</FormHelperText>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <LoadingButton
            loading={loading}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            Submit
          </LoadingButton>
        </Box>

        <Snackbar
          open={snack}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </form>
    </div>
  );
};

export default Login;
